import { useEffect, useRef } from 'react';

interface Dot {
  x: number; y: number; r: number;
  vx: number; vy: number; a: number;
  tier: 0 | 1 | 2;
}

interface Props {
  onAnalysisClick: () => void;
}

function DeviceMockup() {
  return (
    <div className="hero-devices" aria-hidden="true">
      <div className="device-badge device-badge--tl">React</div>
      <div className="device-badge device-badge--tr">WordPress</div>
      <div className="device-badge device-badge--bc">50+ projektov</div>
      <div className="device-glow" />
      <div className="laptop-wrap">
        <div className="laptop-frame">
          <div className="laptop-screen">
            <div className="screen-preview">
              <div className="sp-nav">
                <div className="sp-logo" />
                <div className="sp-nav-links">
                  <div className="sp-link" /><div className="sp-link" /><div className="sp-link" />
                </div>
                <div className="sp-btn" />
              </div>
              <div className="sp-hero">
                <div className="sp-h1" /><div className="sp-h1 sp-h1--short" />
                <div className="sp-sub" /><div className="sp-sub sp-sub--short" />
                <div className="sp-cta-row">
                  <div className="sp-cta-primary" /><div className="sp-cta-ghost" />
                </div>
              </div>
              <div className="sp-cards">
                <div className="sp-card" /><div className="sp-card" /><div className="sp-card" />
              </div>
            </div>
          </div>
          <div className="laptop-chin"><div className="laptop-camera" /></div>
        </div>
        <div className="laptop-base"><div className="laptop-hinge" /></div>
      </div>
      <div className="phone-wrap">
        <div className="phone-frame">
          <div className="phone-notch" />
          <div className="phone-screen">
            <div className="ps-header" />
            <div className="ps-card" /><div className="ps-card ps-card--sm" />
            <div className="ps-btn" />
          </div>
        </div>
      </div>
    </div>
  );
}

const H1_LINE1 = ['Váš', 'web', 'nestačí.'];
const H1_LINE2 = ['My', 'ho', 'zmeníme', 'za', '7', 'dní.'];

const TRUST_ITEMS = [
  {
    text: '50+ spokojných zákazníkov',
    path: <path d="M9 12l2 2 4-4M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>,
  },
  {
    text: 'Dodanie za 7 dní',
    path: <><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></>,
  },
  {
    text: 'React & WordPress Expert',
    path: <path d="M13 10V3L4 14h7v7l9-11h-7z"/>,
  },
  {
    text: '100% spokojnosť',
    path: <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"/>,
  },
] as const;

export default function Hero({ onAnalysisClick }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animId: number;
    let W = 0, H = 0, dots: Dot[] = [];

    // Shooting star state
    const star = { x: 0, y: 0, vx: 0, vy: 0, life: 0, active: false };
    const STAR_LIFE = 55;
    const STAR_INTERVAL = 480; // ~8s at 60fps
    let starTimer = 160; // first star appears after ~2.7s

    function makeDot(): Dot {
      const tier = (Math.random() < 0.6 ? 0 : Math.random() < 0.65 ? 1 : 2) as 0 | 1 | 2;
      const spd   = [0.12, 0.25, 0.45][tier];
      return {
        x: Math.random() * W,
        y: Math.random() * H,
        r:  [0.6, 1.3, 2.2][tier],
        vx: (Math.random() - 0.5) * spd * 2,
        vy: (Math.random() - 0.5) * spd * 2,
        a:  [0.2, 0.4, 0.6][tier] * (0.7 + Math.random() * 0.3),
        tier,
      };
    }

    function resize() {
      W = canvas!.width  = canvas!.offsetWidth;
      H = canvas!.height = canvas!.offsetHeight;
    }

    function launchStar() {
      const angle = (15 + Math.random() * 25) * (Math.PI / 180);
      const spd   = 7 + Math.random() * 3;
      star.x = Math.random() * W * 0.55;
      star.y = Math.random() * H * 0.4;
      star.vx = Math.cos(angle) * spd;
      star.vy = Math.sin(angle) * spd;
      star.life = 0;
      star.active = true;
    }

    function draw() {
      ctx!.clearRect(0, 0, W, H);

      // Particles — 3 tiers
      for (const d of dots) {
        d.x += d.vx; d.y += d.vy;
        if (d.x < 0) d.x = W; else if (d.x > W) d.x = 0;
        if (d.y < 0) d.y = H; else if (d.y > H) d.y = 0;
        ctx!.beginPath();
        ctx!.arc(d.x, d.y, d.r, 0, Math.PI * 2);
        ctx!.fillStyle = d.tier === 2
          ? `rgba(255,69,0,${d.a})`
          : `rgba(255,255,255,${d.a})`;
        ctx!.fill();
      }

      // Shooting star
      starTimer++;
      if (starTimer >= STAR_INTERVAL && !star.active) {
        launchStar();
        starTimer = 0;
      }
      if (star.active) {
        star.life++;
        star.x += star.vx;
        star.y += star.vy;
        const t     = star.life / STAR_LIFE;
        const alpha = t < 0.2 ? (t / 0.2) * 0.85 : (1 - t) * 0.85;
        const trailLen = 55;
        const spd   = Math.sqrt(star.vx ** 2 + star.vy ** 2);
        const nx    = star.vx / spd;
        const ny    = star.vy / spd;
        const g     = ctx!.createLinearGradient(
          star.x - nx * trailLen, star.y - ny * trailLen, star.x, star.y
        );
        g.addColorStop(0, 'rgba(255,255,255,0)');
        g.addColorStop(1, `rgba(255,255,255,${alpha})`);
        ctx!.beginPath();
        ctx!.moveTo(star.x - nx * trailLen, star.y - ny * trailLen);
        ctx!.lineTo(star.x, star.y);
        ctx!.strokeStyle = g;
        ctx!.lineWidth   = 1.5;
        ctx!.stroke();
        if (star.life >= STAR_LIFE) star.active = false;
      }

      animId = requestAnimationFrame(draw);
    }

    window.addEventListener('resize', resize);
    resize();
    dots = Array.from({ length: 90 }, makeDot);
    draw();
    return () => { cancelAnimationFrame(animId); window.removeEventListener('resize', resize); };
  }, []);

  return (
    <section id="hero" aria-labelledby="hero-heading">
      <canvas ref={canvasRef} className="particles-canvas" aria-hidden="true" />
      <div className="hero-split container">

        {/* LEFT — copy */}
        <div className="hero-copy">

          {/* Badge */}
          <div className="hero-badge hero-anim" style={{ animationDelay: '0.1s' }}>
            <span className="dot" aria-hidden="true" />
            Slovenská digitálna agentúra · Kamil Frajtko
          </div>

          {/* Headline — word by word */}
          <h1 id="hero-heading" className="hero-h1">
            <span className="hero-h1-line">
              {H1_LINE1.map((w, i) => (
                <span
                  key={w}
                  className="word-reveal"
                  style={{ animationDelay: `${0.30 + i * 0.08}s`, marginRight: i < H1_LINE1.length - 1 ? '0.22em' : 0 }}
                >
                  {w}
                </span>
              ))}
            </span>
            <em>
              <span className="hero-h1-line">
                {H1_LINE2.map((w, i) => (
                  <span
                    key={w}
                    className="word-reveal"
                    style={{ animationDelay: `${0.54 + i * 0.08}s`, marginRight: i < H1_LINE2.length - 1 ? '0.22em' : 0 }}
                  >
                    {w}
                  </span>
                ))}
              </span>
            </em>
          </h1>

          {/* Subheadline */}
          <p className="hero-sub hero-anim" style={{ animationDelay: '1.5s' }}>
            Weby, e-shopy, správa marketingu, sociálne siete a CRM systémy – všetko pod jednou strechou.
          </p>

          {/* CTAs */}
          <div className="hero-ctas">
            <button
              className="btn-primary hero-btn-anim"
              style={{ animationDelay: '1.8s' }}
              onClick={onAnalysisClick}
            >
              Chcem bezplatnú analýzu →
            </button>
            <a
              href="#projekty"
              className="btn-secondary hero-btn-anim"
              style={{ animationDelay: '1.9s' }}
            >
              Pozrieť projekty
            </a>
          </div>

          {/* Trust badges */}
          <div className="hero-trust">
            {TRUST_ITEMS.map(({ text, path }, i) => (
              <div
                key={text}
                className="trust-badge hero-badge-item-anim"
                style={{ animationDelay: `${2.1 + i * 0.05}s` }}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  {path}
                </svg>
                {text}
              </div>
            ))}
          </div>
        </div>

        {/* RIGHT — device mockup */}
        <DeviceMockup />
      </div>
    </section>
  );
}
